import {
  Genre,
  InstrumentType,
  Playlist,
  Path,
  UpNextProgress,
} from '@fenderdigital/api-definitions/play';
import { LastActivity, LastActivityData } from '../contexts/LastActivityContext';
import isEmpty from './lodash-utils';

export const isRockGuitarPath = (instrument: InstrumentType, style: Genre): boolean => {
  return (instrument === 'electric-guitar' || instrument === 'acoustic-guitar') && style === 'rock';
};

export type Instrument = 'guitar' | 'bass' | 'ukulele';

export interface CurrentLevel {
  slug: string;
  name: string;
}

/**
 * Remove hyphens(-) from instrument data returned back from path API
 *
 * @param {string} instrument
 * @returns {string}
 */
export const formatInstrumentTitle = (instrument?: string): string => {
  if (!instrument) {
    return '';
  }

  return instrument.split('-').join(' ');
};

export const getInstrumentFromPath = (path?: Path | null): Instrument | undefined => {
  if (!path?.instrument || path.instrument === 'unassigned') {
    return undefined;
  }

  return path?.instrument === 'acoustic-guitar' || path?.instrument === 'electric-guitar'
    ? 'guitar'
    : path?.instrument;
};

export const getFirstInstrumentLessonPath = (playlists?: Playlist[], slug?: string): string => {
  if (!playlists || !slug) {
    return '/path';
  }
  const { slug: courseSlug, lessons } = playlists[0]?.courses[0];
  const { slug: lessonSlug } = lessons[0];
  const path =
    courseSlug && lessonSlug
      ? `/path/${slug}/level-1/course/${courseSlug}/lesson/${lessonSlug}`
      : '/path';
  return path;
};

export const getFirstRockGuitarLessonPath = (
  playlists: Playlist[],
  instrument: InstrumentType,
): string => {
  const { slug: courseSlug, lessons } = playlists[0]?.courses[0];
  const { slug: lessonSlug } = lessons[0];
  const path =
    courseSlug && lessonSlug
      ? `/path/rock-${instrument}/level-1/course/${courseSlug}/lesson/${lessonSlug}`
      : '/path';
  return path;
};

export const getCurrentLevel = (
  playlists?: Playlist[],
  lastActivityData?: LastActivityData | null,
): CurrentLevel => {
  const result = {
    slug: '',
    name: 'No level found',
  };

  if (!playlists || !lastActivityData || !lastActivityData['last-activity']) return result;

  const lastActivityPlaylistId = lastActivityData['last-activity']['playlist-id'];
  const currentPlaylist = playlists.find(playlist => playlist.id === lastActivityPlaylistId);

  if (currentPlaylist) {
    result.name = currentPlaylist.name;
    result.slug = currentPlaylist.slug;
  }
  
  return result;
};

export const constructCoursePath = (upNextData: UpNextProgress | null): string => {
  if (
    isEmpty(upNextData) ||
    (!upNextData?.['path-slug'] && !upNextData?.['path-level'] && !upNextData?.['course-slug'])
  ) {
    return '/path';
  }

  return `/path/${upNextData['path-slug']}/level-${upNextData['path-level']}/course/${upNextData['course-slug']}`;
};

export const isAtFirstRockGuitarLesson = (
  playlists: Playlist[],
  instrument: InstrumentType,
): boolean => {
  return window.location.pathname.includes(getFirstRockGuitarLessonPath(playlists, instrument));
};

export const getLastActivityCoursePath = (lastActivity: LastActivity): string => {
  if (!lastActivity) return '/path';

  const {
    'path-slug': pathSlug, 
    'playlist-slug': playlistSlug, 
    'course-slug': courseSlug
  } = lastActivity;

  if (pathSlug && playlistSlug && courseSlug)
    return `/path/${pathSlug}/${playlistSlug}/course/${courseSlug}`;
  else if (courseSlug)
    return `/course/${courseSlug}`;
  
  return `/path`;
};

export const getStandaloneLastActivityPath = (lastActivity: LastActivity): string => {
  if (!lastActivity) return '/path';

  const { 'activity-slug': activitySlug } = lastActivity;
  const activityType = lastActivity['activity-type'] === 'practice-sheet' ? 'practice' : 'lesson';

  return `/${activityType}/${activitySlug}`;
};
